import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { Send, MessageSquare, Mail, Sparkles } from "lucide-react";

export default function CrmPage() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [feedback, setFeedback] = useState([]);
  const [regs, setRegs] = useState([]);
  const [summary, setSummary] = useState("");
  const [summarizing, setSummarizing] = useState(false);
  const [notifForm, setNotifForm] = useState({
    audience: "all_delegates",
    channel: "email",
    subject: "Registration reminder",
    body: "Hi delegate, don't forget to complete your registration and submit your position paper!",
  });
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    api.get("/events").then((r) => {
      setEvents(r.data);
      if (r.data[0]) setSelectedEvent(r.data[0].id);
    });
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedEvent) return;
    api.get(`/feedback?event_id=${selectedEvent}`).then((r) => setFeedback(r.data));
    api.get(`/registrations?event_id=${selectedEvent}`).then((r) => setRegs(r.data));
  }, [selectedEvent]);

  const summarize = async () => {
    setSummarizing(true);
    try {
      const { data } = await api.post("/feedback/summarize", { event_id: selectedEvent });
      setSummary(data.summary);
      toast.success("AI summary ready");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
    setSummarizing(false);
  };

  const sendNotif = async (e) => {
    e.preventDefault();
    try {
      await api.post("/notifications/send", { ...notifForm, event_id: selectedEvent });
      toast.success(`${notifForm.channel.toUpperCase()} sent (mock)`);
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Delegate CRM
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Engage & retain
            </h1>
          </div>
          <Select value={selectedEvent} onValueChange={setSelectedEvent}>
            <SelectTrigger className="w-[240px]" data-testid="crm-event-select"><SelectValue placeholder="Select event" /></SelectTrigger>
            <SelectContent>
              {events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="comms" className="mt-10">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="comms" data-testid="crm-tab-comms">Communications</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="crm-tab-feedback">Feedback</TabsTrigger>
            <TabsTrigger value="delegates" data-testid="crm-tab-delegates">Delegates</TabsTrigger>
          </TabsList>

          <TabsContent value="comms" className="mt-6 grid lg:grid-cols-12 gap-6">
            <form onSubmit={sendNotif} className="lg:col-span-5 bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="notif-form">
              <h3 className="text-lg font-semibold tracking-tight text-[#0A192F]">
                Broadcast
              </h3>
              <div>
                <Label>Audience</Label>
                <Select value={notifForm.audience} onValueChange={(v) => setNotifForm({ ...notifForm, audience: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_delegates">All delegates</SelectItem>
                    <SelectItem value="chairs">Chairs</SelectItem>
                    <SelectItem value="org_admins">Organisers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Channel</Label>
                <Select value={notifForm.channel} onValueChange={(v) => setNotifForm({ ...notifForm, channel: v })}>
                  <SelectTrigger data-testid="notif-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email (mock)</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp (placeholder)</SelectItem>
                    <SelectItem value="in_app">In-app</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject</Label>
                <Input value={notifForm.subject} onChange={(e) => setNotifForm({ ...notifForm, subject: e.target.value })} />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea value={notifForm.body} onChange={(e) => setNotifForm({ ...notifForm, body: e.target.value })} rows={4} />
              </div>
              <Button type="submit" className="w-full bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="notif-send-button">
                <Send className="h-4 w-4 mr-2" /> Send
              </Button>
            </form>

            <div className="lg:col-span-7 bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold tracking-tight text-[#0A192F]">
                Sent log
              </h3>
              <div className="mt-5 space-y-3">
                {notifs.map((n) => (
                  <div key={n.id} className="p-4 rounded-md border border-slate-200 bg-slate-50">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-[#0A192F]">{n.subject}</p>
                      <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#0A192F]">
                        {n.channel}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-2 line-clamp-2">{n.body}</p>
                  </div>
                ))}
                {notifs.length === 0 && (
                  <p className="text-sm text-slate-500">No communications sent yet.</p>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="feedback" className="mt-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h3 className="text-lg font-semibold tracking-tight text-[#0A192F]">Delegate feedback</h3>
              <Button onClick={summarize} disabled={summarizing} className="bg-[#0A192F] hover:bg-[#0F4C81]" data-testid="summarize-feedback-button">
                <Sparkles className="h-4 w-4 mr-2 text-[#D4AF37]" />
                {summarizing ? "Summarising…" : "AI summary"}
              </Button>
            </div>
            {summary && (
              <div className="mt-5 rounded-xl bg-[#0A192F] text-white p-6 border border-[#D4AF37]/30">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">AI Brief</p>
                <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap">{summary}</p>
              </div>
            )}
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              {feedback.map((f) => (
                <div key={f.id} className="p-5 rounded-xl bg-white border border-slate-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-[#0A192F]">Rating: {"★".repeat(f.rating)}</p>
                    <MessageSquare className="h-4 w-4 text-[#D4AF37]" />
                  </div>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.comment}</p>
                </div>
              ))}
              {feedback.length === 0 && <p className="text-sm text-slate-500">No feedback yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="delegates" className="mt-6">
            <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-3">Delegate</th>
                    <th className="text-left px-5 py-3">Email</th>
                    <th className="text-left px-5 py-3">Status</th>
                    <th className="text-left px-5 py-3">Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {regs.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-5 py-3 font-semibold text-[#0A192F]">{r.delegate_name}</td>
                      <td className="px-5 py-3 text-slate-600">{r.delegate_email}</td>
                      <td className="px-5 py-3 uppercase text-[10px] tracking-[0.18em] text-slate-500">{r.status}</td>
                      <td className="px-5 py-3">
                        <span className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-[0.18em] ${r.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-[#D4AF37]/15 text-[#0A192F]"}`}>
                          {r.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {regs.length === 0 && (
                    <tr><td colSpan={4} className="text-center py-10 text-slate-500">No delegates yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-4 text-xs text-slate-500 flex items-center gap-2">
              <Mail className="h-3.5 w-3.5" />
              Tip: Use the Communications tab to broadcast mock emails / WhatsApp.
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
